import React, { useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSuccessionTheme } from '../SuccessionThemeProvider';

const Details = ({ formData, setFormData, partialSaleQuestions, company, onValidationChange }) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const [errors, setErrors] = useState({});

  // States and provinces data
  const usStates = [
    { value: 'Alabama', name: 'Alabama' },
    { value: 'Alaska', name: 'Alaska' },
    { value: 'Arizona', name: 'Arizona' },
    { value: 'Arkansas', name: 'Arkansas' },
    { value: 'California', name: 'California' },
    { value: 'Colorado', name: 'Colorado' },
    { value: 'Connecticut', name: 'Connecticut' },
    { value: 'Delaware', name: 'Delaware' },
    { value: 'Florida', name: 'Florida' },
    { value: 'Georgia', name: 'Georgia' },
    { value: 'Hawaii', name: 'Hawaii' },
    { value: 'Idaho', name: 'Idaho' },
    { value: 'Illinois', name: 'Illinois' },
    { value: 'Indiana', name: 'Indiana' },
    { value: 'Iowa', name: 'Iowa' },
    { value: 'Kansas', name: 'Kansas' },
    { value: 'Kentucky', name: 'Kentucky' },
    { value: 'Louisiana', name: 'Louisiana' },
    { value: 'Maine', name: 'Maine' },
    { value: 'Maryland', name: 'Maryland' },
    { value: 'Massachusetts', name: 'Massachusetts' },
    { value: 'Michigan', name: 'Michigan' },
    { value: 'Minnesota', name: 'Minnesota' },
    { value: 'Mississippi', name: 'Mississippi' },
    { value: 'Missouri', name: 'Missouri' },
    { value: 'Montana', name: 'Montana' },
    { value: 'Nebraska', name: 'Nebraska' },
    { value: 'Nevada', name: 'Nevada' },
    { value: 'New Hampshire', name: 'New Hampshire' },
    { value: 'New Jersey', name: 'New Jersey' },
    { value: 'New Mexico', name: 'New Mexico' },
    { value: 'New York', name: 'New York' },
    { value: 'North Carolina', name: 'North Carolina' },
    { value: 'North Dakota', name: 'North Dakota' },
    { value: 'Ohio', name: 'Ohio' },
    { value: 'Oklahoma', name: 'Oklahoma' },
    { value: 'Oregon', name: 'Oregon' },
    { value: 'Pennsylvania', name: 'Pennsylvania' },
    { value: 'Rhode Island', name: 'Rhode Island' },
    { value: 'South Carolina', name: 'South Carolina' },
    { value: 'South Dakota', name: 'South Dakota' },
    { value: 'Tennessee', name: 'Tennessee' },
    { value: 'Texas', name: 'Texas' },
    { value: 'Utah', name: 'Utah' },
    { value: 'Vermont', name: 'Vermont' },
    { value: 'Virginia', name: 'Virginia' },
    { value: 'Washington', name: 'Washington' },
    { value: 'West Virginia', name: 'West Virginia' },
    { value: 'Wisconsin', name: 'Wisconsin' },
    { value: 'Wyoming', name: 'Wyoming' }
  ];

  const canadianProvinces = [
    { value: 'Alberta', name: 'Alberta' },
    { value: 'British Columbia', name: 'British Columbia' },
    { value: 'Manitoba', name: 'Manitoba' },
    { value: 'New Brunswick', name: 'New Brunswick' },
    { value: 'Newfoundland and Labrador', name: 'Newfoundland and Labrador' },
    { value: 'Northwest Territories', name: 'Northwest Territories' },
    { value: 'Nova Scotia', name: 'Nova Scotia' },
    { value: 'Nunavut', name: 'Nunavut' },
    { value: 'Ontario', name: 'Ontario' },
    { value: 'Prince Edward Island', name: 'Prince Edward Island' },
    { value: 'Quebec', name: 'Quebec' },
    { value: 'Saskatchewan', name: 'Saskatchewan' },
    { value: 'Yukon', name: 'Yukon' }
  ];

  const isUSCompany = company?.country?.toUpperCase() === 'US';
  const locations = isUSCompany ? usStates : canadianProvinces;
  const locationLabel = isUSCompany ? 'States' : 'Provinces';

  // Initialize questions and answers arrays
  useEffect(() => {
    if (partialSaleQuestions && partialSaleQuestions.length > 0) {
      const questions = partialSaleQuestions.map(q => q.question);
      const answers = new Array(partialSaleQuestions.length).fill(false);
      
      setFormData(prev => ({
        ...prev,
        questions,
        answers: prev.answers.length === questions.length ? prev.answers : answers
      }));
    }
  }, [partialSaleQuestions]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.states || formData.states.length === 0) {
      newErrors.states = intl.formatMessage(
        { id: 'succession.partial_sales.details.error_location_required' },
        { locationType: isUSCompany ? 'state' : 'province' }
      );
    }

    setErrors(newErrors);
    const hasErrors = Object.keys(newErrors).length > 0;
    
    // Notify parent component about validation state
    if (onValidationChange) {
      onValidationChange(hasErrors);
    }
    
    return !hasErrors;
  };

  // Validate when states change
  useEffect(() => {
    validateForm();
  }, [formData.states]);

  // Initial validation on mount
  useEffect(() => {
    validateForm();
  }, []);

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...formData.answers];
    newAnswers[index] = value;
    setFormData({
      ...formData,
      answers: newAnswers
    });
  };

  const inputClasses = "w-full px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary focus:border-transparent transition-colors bg-succession-bg-primary text-succession-text-primary";
  const labelClasses = "block text-sm font-medium mb-2 text-succession-text-primary";
  const errorClasses = "mt-1 text-sm text-succession-error";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-succession-text-primary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.details.title" />
        </h2>
        <p className="text-lg mb-6 text-succession-text-secondary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.details.intro" />
        </p>
      </div>

      {/* Geographic Coverage */}
      <div>
        <label className={`${labelClasses} text-succession-text-primary transition-colors duration-300`} style={{paddingBottom: '0px', paddingTop: '0px'}}>
          <FormattedMessage id="succession.partial_sales.details.geographic_coverage_label" />
        </label>
        <p className="text-sm mb-4 text-succession-text-secondary transition-colors duration-300">
          {intl.formatMessage({ id: 'succession.partial_sales.details.select_locations' }, { locations: locationLabel.toLowerCase() })}
        </p>
        
        {errors.states && (
          <div className="mb-4 p-3 bg-succession-error-bg border border-succession-error rounded-lg">
            <p className="text-sm text-succession-error">{errors.states}</p>
          </div>
        )}
        {formData.states && formData.states.length > 0 && (
          <div className="mt-3">
            <p className="text-sm text-succession-text-secondary transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.partial_sales.details.selected_label' })} {formData.states.join(', ')}
            </p>
            <br />
          </div>
        )}

        <div className="max-h-60 overflow-y-auto border rounded-lg p-4 border-succession-border transition-colors duration-300">
          <div className="grid grid-cols-1 gap-2">
            {locations.map((location) => {
              const isSelected = (formData.states || []).includes(location.value);
              return (
                <div
                  key={location.value}
                  onClick={() => {
                    const currentStates = formData.states || [];
                    const newStates = isSelected
                      ? currentStates.filter(s => s !== location.value)
                      : [...currentStates, location.value];
                    
                    setFormData({
                      ...formData,
                      states: newStates
                    });
                    
                    // Clear error when user selects states
                    if (errors.states && newStates.length > 0) {
                      setErrors({
                        ...errors,
                        states: ''
                      });
                    }
                  }}
                  className={`
                    flex items-center justify-between p-2 rounded cursor-pointer transition-colors duration-200
                    ${isSelected 
                      ? 'bg-succession-primary-bg text-succession-primary font-medium border border-succession-primary' 
                      : 'hover:bg-succession-bg-secondary text-succession-text-secondary border border-transparent'
                    }
                  `}
                >
                  <span className="text-sm">{location.name}</span>
                  {isSelected && (
                    <svg className="w-4 h-4 text-succession-primary flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Additional Details Fields - only show if feature enabled */}
      {company?.feature_types?.includes('partial_sales_detailed_questions') && (
        <div className="space-y-6">
          {/* Client Demographics */}
          <div>
            <label className={`${labelClasses} text-succession-text-primary transition-colors duration-300`} style={{paddingBottom: '0px', paddingTop: '0px'}}>
              <FormattedMessage id="succession.partial_sales.details.client_demographics_label" />
            </label>
            <textarea
              value={formData.clientDemographicsDetails || ''}
              onChange={(e) => {
                if (e.target.value.length <= 250) {
                  setFormData({
                    ...formData,
                    clientDemographicsDetails: e.target.value
                  });
                }
              }}
              className={`${inputClasses}`}
              rows={4}
              placeholder={intl.formatMessage({ id: 'succession.partial_sales.details.client_demographics_placeholder' })}
            />
            <p className="mt-1 text-sm text-succession-text-muted">
              {(formData.clientDemographicsDetails || '').length}/250 characters
            </p>
          </div>

          {/* Services Provided */}
          <div>
            <label className={`${labelClasses} text-succession-text-primary transition-colors duration-300`} style={{paddingBottom: '0px', paddingTop: '0px'}}>
              <FormattedMessage id="succession.partial_sales.details.services_provided_label" />
            </label>
            <textarea
              value={formData.servicesProvidedDetails || ''}
              onChange={(e) => {
                if (e.target.value.length <= 250) {
                  setFormData({
                    ...formData,
                    servicesProvidedDetails: e.target.value
                  });
                }
              }}
              className={`${inputClasses}`}
              rows={4}
              placeholder={intl.formatMessage({ id: 'succession.partial_sales.details.services_provided_placeholder' })}
            />
            <p className="mt-1 text-sm text-succession-text-muted">
              {(formData.servicesProvidedDetails || '').length}/250 characters
            </p>
          </div>

          {/* Other Details */}
          <div>
            <label className={`${labelClasses} text-succession-text-primary transition-colors duration-300`} style={{paddingBottom: '0px', paddingTop: '0px'}}>
              <FormattedMessage id="succession.partial_sales.details.other_label" />
            </label>
            <textarea
              value={formData.otherDetails || ''}
              onChange={(e) => {
                if (e.target.value.length <= 250) {
                  setFormData({
                    ...formData,
                    otherDetails: e.target.value
                  });
                }
              }}
              className={`${inputClasses}`}
              rows={4}
              placeholder={intl.formatMessage({ id: 'succession.partial_sales.details.other_placeholder' })}
            />
            <p className="mt-1 text-sm text-succession-text-muted">
              {(formData.otherDetails || '').length}/250 characters
            </p>
          </div>
        </div>
      )}

      {/* Questions Section */}
      {partialSaleQuestions && partialSaleQuestions.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4 text-succession-text-primary transition-colors duration-300">
            <FormattedMessage id="succession.partial_sales.details.additional_questions_title" />
          </h3>
          <p className="text-sm mb-4 text-succession-text-secondary transition-colors duration-300">
            <FormattedMessage id="succession.partial_sales.details.additional_questions_intro" />
          </p>

          <div className="space-y-4">
            {partialSaleQuestions.map((question, index) => (
              <div key={question.id || index} className="bg-succession-bg-secondary p-4 rounded-lg border border-succession-border">
                <p className="font-medium mb-3 text-succession-text-primary transition-colors duration-300">
                  {question.question}
                </p>
                <div className="flex space-x-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`question-${index}`}
                      checked={formData.answers[index] === true}
                      onChange={() => handleAnswerChange(index, true)}
                      className="h-4 w-4 border-succession-border focus:ring-succession-primary"
                      style={{
                        accentColor: 'var(--succession-primary)'
                      }}
                    />
                    <span className="text-sm text-succession-text-primary transition-colors duration-300">
                      <FormattedMessage id="succession.partial_sales.details.yes" />
                    </span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={`question-${index}`}
                      checked={formData.answers[index] === false}
                      onChange={() => handleAnswerChange(index, false)}
                      className="h-4 w-4 border-succession-border focus:ring-succession-primary"
                      style={{
                        accentColor: 'var(--succession-primary)'
                      }}
                    />
                    <span className="text-sm text-succession-text-primary transition-colors duration-300">
                      <FormattedMessage id="succession.partial_sales.details.no" />
                    </span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Details;